import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ExternalLink, Download, Receipt, RefreshCw, AlertCircle } from "lucide-react";
import { useLang } from "@/context/LangContext";

/**
 * Sprint 92ah — Stripe invoices list (shared component).
 *
 * Renders the invoice history for a given member/company. Used both in the
 * admin company detail page (via `endpoint="/admin/companies/{id}/invoices"`)
 * and in the member portal (via `endpoint="/portal/my-invoices"`).
 */
const STATUS_META = {
  paid: "bg-emerald-50 text-emerald-700",
  open: "bg-amber-50 text-amber-700",
  pending: "bg-amber-50 text-amber-700",
  draft: "bg-neutral-100 text-neutral-600",
  void: "bg-neutral-100 text-neutral-500 line-through",
  uncollectible: "bg-rose-50 text-rose-700",
};

export default function InvoicesList({ endpoint, title, emptyLabel }) {
  const { t, lang } = useLang();
  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString(lang, { day: "2-digit", month: "short", year: "numeric" }) : "—";
  const fmtMoney = (v, ccy) => new Intl.NumberFormat(lang, { style: "currency", currency: (ccy || "eur").toUpperCase() }).format(v || 0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = () => {
    if (!endpoint) return;
    setLoading(true);
    api.get(endpoint)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || t("inv_load_error")))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [endpoint]);

  const invoices = data?.invoices || [];

  return (
    <div className="rounded-2xl border border-neutral-100 bg-white p-6" data-testid="invoices-block">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-bold text-navy inline-flex items-center gap-2">
          <Receipt className="h-5 w-5 text-wine-500" /> {title || t("inv_title")}
          {data && <span className="ml-2 rounded-full bg-neutral-100 px-2 py-0.5 text-[10px] font-bold text-neutral-600">{invoices.length}</span>}
        </h2>
        <div className="flex items-center gap-3">
          {data?.total_paid > 0 && (
            <span className="text-xs text-neutral-600">
              {t("inv_total_paid")} <strong className="text-emerald-700">{fmtMoney(data.total_paid, data.currency)}</strong>
            </span>
          )}
          <button
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-1 rounded-full border border-neutral-200 bg-white px-3 py-1.5 text-xs font-semibold text-neutral-700 hover:bg-neutral-50 disabled:opacity-50"
            data-testid="invoices-refresh"
            title={t("inv_refresh_title")}
          >
            <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} /> {t("inv_refresh")}
          </button>
        </div>
      </div>

      {data && !data.stripe_configured && (
        <div className="mt-3 flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="invoices-stripe-warning">
          <AlertCircle className="h-4 w-4" />
          <span>{t("inv_stripe_not_configured")}</span>
        </div>
      )}

      {loading && invoices.length === 0 && (
        <div className="mt-4 rounded-xl bg-neutral-50 py-8 text-center text-sm text-neutral-500">{t("loading")}</div>
      )}

      {!loading && invoices.length === 0 && (
        <div className="mt-4 rounded-xl bg-neutral-50 py-8 text-center text-sm text-neutral-500">{emptyLabel || t("inv_empty")}</div>
      )}

      {invoices.length > 0 && (
        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full text-sm" data-testid="invoices-table">
            <thead>
              <tr className="border-b border-neutral-100 text-left text-[10px] font-bold uppercase tracking-wider text-neutral-500">
                <th className="pb-2">{t("inv_col_date")}</th>
                <th className="pb-2">{t("inv_col_number")}</th>
                <th className="pb-2">{t("inv_col_description")}</th>
                <th className="pb-2 text-right">{t("inv_col_amount")}</th>
                <th className="pb-2">{t("inv_col_status")}</th>
                <th className="pb-2 text-right">{t("inv_col_actions")}</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => {
                const meta = STATUS_META[inv.status]
                  ? { label: t(`inv_status_${inv.status}`), cls: STATUS_META[inv.status] }
                  : { label: inv.status, cls: "bg-neutral-100 text-neutral-600" };
                return (
                  <tr key={inv.id} className="border-b border-neutral-50 last:border-0" data-testid={`invoice-row-${inv.id}`}>
                    <td className="py-3 text-neutral-700">{fmtDate(inv.created_at)}</td>
                    <td className="py-3 font-mono text-xs text-neutral-600">{inv.number}</td>
                    <td className="py-3 text-neutral-600">{inv.description || (inv.source === "checkout" ? t("inv_checkout_session") : "—")}</td>
                    <td className="py-3 text-right font-bold text-navy">{fmtMoney(inv.amount, inv.currency)}</td>
                    <td className="py-3">
                      <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-bold ${meta.cls}`}>{meta.label}</span>
                    </td>
                    <td className="py-3">
                      <div className="flex justify-end gap-1.5">
                        {inv.hosted_invoice_url && (
                          <a
                            href={inv.hosted_invoice_url}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 rounded-full border border-wine-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-wine-700 hover:bg-wine-50"
                            data-testid={`invoice-view-${inv.id}`}
                            title={inv.source === "checkout" ? t("inv_open_payment") : t("inv_view_stripe")}
                          >
                            <ExternalLink className="h-3 w-3" />
                            {inv.source === "checkout" ? t("inv_pay") : t("inv_view")}
                          </a>
                        )}
                        {inv.invoice_pdf && (
                          <a
                            href={inv.invoice_pdf}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 rounded-full border border-neutral-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-neutral-700 hover:bg-neutral-50"
                            data-testid={`invoice-pdf-${inv.id}`}
                            title={t("inv_download_pdf")}
                          >
                            <Download className="h-3 w-3" /> PDF
                          </a>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
